import { Component, inject, input, output, OnInit } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ImagesService } from '../../../../services/general/images.service';
import { GroupedGuarantor, Loan, User } from '../../../../interfaces/responses/loan.interface';
import { CommonModule, DatePipe, DecimalPipe, formatDate } from '@angular/common';
import { LoanStatusStylePipe, LoanStatusLabelPipe } from '../../../../pipes/loan-status.pipe';
import { GuarantorService } from '../../../../services/guarantor/guarantor.service';
import { ToastService } from '../../../../services/general/toast.service';
import { LoanService } from '../../../../services/loan/loan.service';

@Component({
  selector: 'app-view-new-application',
  imports: [CommonModule, DatePipe, DecimalPipe, LoanStatusStylePipe, LoanStatusLabelPipe],
  templateUrl: './view-new-application.component.html',
  styleUrl: './view-new-application.component.scss'
})
export class ViewNewApplicationComponent implements OnInit {
  _images = inject(ImagesService);
  private sanitizer = inject(DomSanitizer);
  private guarantorService = inject(GuarantorService);
  private loanService = inject(LoanService);
  private toast = inject(ToastService);

  loanInfo = input.required<Loan>();
  showHeader = input<boolean>(true);
  monthlyIncome: number = 0;
  loanApplicantInfo!: User | null;

  onClose = output<void>();

  showDiscrepancies: boolean = false;
  smsSendingTokens = new Set<string>();
  isSendingEmail = false;

  previewUrl: string | null = null;
  safePreviewUrl: SafeResourceUrl | null = null;

  getTenureUnit(tenureType?: string, tenure?: string | number): string {
    const unit = tenureType === 'DAILY' ? 'Day' : tenureType === 'WEEKLY' ? 'Week' : 'Month';
    return `${unit}${Number(tenure) !== 1 ? 's' : ''}`;
  }

  getRepaymentLabel(tenureType?: string): string {
    const type = (tenureType || 'MONTHLY').toLowerCase();
    return `${type.charAt(0).toUpperCase()}${type.slice(1)} Repayment`;
  }

  ngOnInit(): void {
    this.loanApplicantInfo = this.loanInfo().user;
    this.monthlyIncome = this.loanInfo().userMonthlyIncome;
  }

  onCloseClick() {
    this.onClose.emit();
  }

  openPreview(url: string) {
    if (!this.isSafeUrl(url)) return;
    this.previewUrl = url;
    this.safePreviewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  private isSafeUrl(url: string): boolean {
    try {
      const parsed = new URL(url);
      return parsed.protocol === 'https:' || parsed.protocol === 'http:';
    } catch {
      return false;
    }
  }

  closePreview() {
    this.previewUrl = null;
    this.safePreviewUrl = null;
  }

  async downloadFile(url: string, index: number) {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const ext = this.docExtension(url);
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = `document-${index}.${ext}`;
      anchor.click();
      URL.revokeObjectURL(objectUrl);
    } catch {
      window.open(url, '_blank');
    }
  }

  isImage(url: string): boolean {
    return /\.(jpg|jpeg|png|gif|webp|svg)(\?.*)?$/i.test(url);
  }

  isPdf(url: string): boolean {
    return /\.pdf(\?.*)?$/i.test(url);
  }

  docExtension(url: string): string {
    const match = url.match(/\.([a-zA-Z0-9]+)(\?.*)?$/);
    return match ? match[1] : 'file';
  }

  getActiveDiscrepancies(group: GroupedGuarantor): string[] {
    return this.showDiscrepancies ? this.getDiscrepancies(group) : [];
  }

  getDiscrepancies(group: GroupedGuarantor): string[] {
    if (!group.guarantor) return [];
    const differ = (a: any, b: any) => {
      if (a == null || b == null) return false;
      return String(a).trim().toLowerCase() !== String(b).trim().toLowerCase();
    };
    const mismatches: string[] = [];
    if (differ(group.applicant.name, group.guarantor.name)) mismatches.push('name');
    if (differ(group.applicant.phone, group.guarantor.phone)) mismatches.push('phone');
    if (differ(group.applicant.relationship, group.guarantor.relationship)) mismatches.push('relationship');
    if (differ(group.applicant.digitalAddress, group.guarantor.digitalAddress)) mismatches.push('digitalAddress');
    if (differ(group.applicant.workPlace, group.guarantor.workPlace)) mismatches.push('workPlace');
    if (differ(group.applicant.workRole, group.guarantor.workRole)) mismatches.push('workRole');
    return mismatches;
  }

  sendSms(token: string, loanId: string, name: string) {
    if (this.smsSendingTokens.has(token)) return;
    this.smsSendingTokens.add(token);
    this.guarantorService.sendSms(token, loanId).subscribe({
      next: () => {
        this.smsSendingTokens.delete(token);
        this.toast.success(`SMS sent successfully to ${name}.`);
      },
      error: () => {
        this.smsSendingTokens.delete(token);
        this.toast.error('Failed to send SMS. Please try again.');
      }
    });
  }

  sendDisbursementEmail() {
    const id = this.loanInfo().id;
    if (!id || this.isSendingEmail) return;
    this.isSendingEmail = true;
    this.loanService.resendDisbursementEmail(id).subscribe({
      next: () => {
        this.isSendingEmail = false;
        this.toast.success('Disbursement email sent successfully.');
      },
      error: () => {
        this.isSendingEmail = false;
        this.toast.error('Failed to send email. Please try again.');
      }
    });
  }

  private escapeHtml(value: unknown): string {
    const str = value === null || value === undefined ? '' : String(value);
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#x27;');
  }

  downloadDisbursementPDF() {
    const loan = this.loanInfo();
    const user = this.loanApplicantInfo;
    const ref = this.escapeHtml(loan.reference ?? (loan.id ? loan.id.substring(0, 8).toUpperCase() : 'N/A'));
    const institutionName = this.escapeHtml((loan.institution as any)?.name || loan.institution || 'Direct Savings & Loans');
    const fullName = this.escapeHtml([user?.firstName, user?.otherName, user?.lastName].filter(Boolean).join(' ').toUpperCase());
    const productName = this.escapeHtml(loan.product?.name || '—');
    const ghanaCard = this.escapeHtml(loan.userGhanaCard || user?.ghanaCardNumber || '—');

    const fmtDate = (d: any) => d ? formatDate(d, 'yyyy-MM-dd', 'en-GH') : '—';
    const fmtAmt = (val: any) => Number(val || 0).toLocaleString('en-GH', { minimumFractionDigits: 1, maximumFractionDigits: 2 });

    const loanAmount   = fmtAmt(loan.principal_amount || loan.amount);
    const totalPayable = fmtAmt(loan.totalRepayment);
    const monthlyDed   = fmtAmt(loan.monthlyRepayment);
    const interestRate = loan.interestRate ?? '—';
    const interestRateLabel = loan.product?.tenureType === 'DAILY' ? 'Daily Rate' : 'Annual Interest Rate';
    const procFeeRate  = loan.processingFeeRate ?? '—';
    const tenure       = loan.tenure ?? '—';
    const startDate    = fmtDate(loan.disbursedAt);
    const endDate      = fmtDate(loan.repaymentDate);


    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>${ref}</title>
  <style>
    @media print { @page { size: A4 portrait; margin: 20mm 18mm; } }
    body { font-family: Arial, sans-serif; font-size: 12px; color: #000; margin: 0; padding: 32px 40px; line-height: 1.5; }
    p { margin: 0 0 10px; }
    .app-number { font-weight: bold; font-size: 13px; margin-bottom: 16px; }
    .app-number span { background: #FFE066; padding: 1px 4px; }
    .section-heading { font-weight: bold; font-size: 13px; margin: 20px 0 8px; }
    table.loan-table { width: 70%; border-collapse: collapse; margin-bottom: 16px; }
    table.loan-table td { border: 1px solid #000; padding: 7px 10px; font-size: 12px; vertical-align: middle; }
    table.loan-table td.label { background: #fff; font-weight: normal; width: 25%; }
    table.loan-table td.value { font-weight: bold; width: 25%; }
    table.loan-table tr.date-row td { font-size: 16px; }
    table.loan-table tr.date-row td.label { font-size: 16px; font-weight: normal; }
    .signoff { margin-top: 28px; }
  </style>
</head>
<body>

  <div class="app-number">Application Number: <span>${ref}</span></div>
  <p>Dear ${fullName},</p>

  <p>We are pleased to welcome you to the ${institutionName} family and are happy to inform you that your ${institutionName} ${productName} application has been approved.</p>

  <p>Reaching this stage of the process confirms that you have read, understood, and agreed to all the Terms and Conditions associated with this application. Kindly ensure that you carefully review the loan details below and fully understand all costs related to your loan. Please note that ${institutionName}'s Terms and Conditions form an integral part of your Loan Agreement and must be read in conjunction with the Loan Details.</p>

  <div class="section-heading">Loan Details:</div>

  <table class="loan-table">
    <tr>
      <td class="label">Loan Account Number:</td>
      <td class="value">${ref}</td>
      <td class="label">Product Type:</td>
      <td class="value">${productName}</td>
    </tr>
    <tr>
      <td class="label">Ghana Card Number:</td>
      <td class="value" colspan="3">${ghanaCard}</td>
    </tr>
    <tr>
      <td class="label">Loan Amount:</td>
      <td class="value">GHS ${loanAmount}</td>
      <td class="label">Total Amount Payable:</td>
      <td class="value">GHS ${totalPayable}</td>
    </tr>
    <tr>
      <td class="label">${interestRateLabel}:</td>
      <td class="value">${interestRate}%</td>
      <td class="label">Processing Fee:</td>
      <td class="value">${procFeeRate}%</td>
    </tr>
    <tr>
      <td class="label">Loan Term:</td>
      <td class="value">${tenure} months</td>
      <td class="label">Monthly Deduction Amount:</td>
      <td class="value">GHS ${monthlyDed}</td>
    </tr>
    <tr class="date-row">
      <td class="label">Start Date:</td>
      <td class="value">${startDate}</td>
      <td class="label">End Date:</td>
      <td class="value">${endDate}</td>
    </tr>
  </table>

  <div class="section-heading">Important Notice</div>
  <p>Please note that in the event you abandon, vacate, or are unable to continue your employment, or if ${institutionName} is unable to receive deductions as agreed, the applicant/customer shall bear all costs incurred in recovering the loan in the case of default.</p>

  <div class="section-heading">Data Privacy</div>
  <p>We respect your privacy and will use your personal data strictly in accordance with our Terms and Conditions and Privacy Policy.</p>
  <p>Should you have any questions or require further assistance, kindly contact us.</p>

  <div class="section-heading">Declaration</div>
  <p>I, ${fullName}, hereby confirm that I have applied for the loan amount fully disclosed above. I understand and consent to the terms and conditions of the Loan Agreement and am fully aware of all costs and repayment details.</p>
  <p>I confirm that I have read, understood, and agree to the ${institutionName} Terms and Conditions, and hereby authorize my employer and/or bank (Direct Debit) to deduct the monthly installments stated above until the total contractual amount payable has been fully settled.</p>

  <div class="signoff">
    <p>Yours sincerely,</p>
    <p><strong>${institutionName}</strong></p>
  </div>

</body>
</html>`;

    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:0;height:0;border:0;';
    document.body.appendChild(iframe);
    const doc = iframe.contentDocument!;
    doc.open();
    doc.write(html);
    doc.close();
    iframe.onload = () => {
      iframe.contentWindow!.focus();
      iframe.contentWindow!.print();
      setTimeout(() => document.body.removeChild(iframe), 1000);
    };
  }

  get groupedGuarantors(): GroupedGuarantor[] {
    const guarantors = this.loanInfo().guarantors ?? [];
    const map = new Map<string, GroupedGuarantor>();
    for (const g of guarantors) {
      if (!map.has(g.token)) {
        map.set(g.token, { token: g.token, applicant: g, guarantor: null });
      }
      const group = map.get(g.token)!;
      if (g.signature === null) {
        group.applicant = g;
      } else {
        group.guarantor = g;
      }
    }
    return Array.from(map.values());
  }
}
